import React, { useMemo } from 'react'
import type { SecurityOverview } from '../../api/security'
import StatusPill, { PillTone } from './StatusPill'

export default function SecurityStatusCard({ overview }: { overview: SecurityOverview | null }) {
  const rows = useMemo(() => {
    if (!overview) return [] as Array<{ label: string; value: string; tone: PillTone; description?: string }>
    return [
      {
        label: 'Email',
        value: overview.email_masked || 'Not added',
        tone: overview.email_verified ? ('success' as const) : ('action' as unknown as PillTone),
        description: overview.email_verified ? 'Verified for login and recovery' : 'Verify your email to secure the account',
      },
      {
        label: 'Phone',
        value: overview.phone_masked || 'Not added',
        tone: overview.phone ? ('success' as const) : ('info' as const),
        description: overview.phone ? 'Used for alerts & recovery' : 'Add a phone for recovery alerts',
      },
      {
        label: 'Password',
        value: overview.has_password ? 'Password set' : 'Not set',
        tone: overview.has_password ? ('success' as const) : ('action' as unknown as PillTone),
        description: overview.has_password ? 'Change via the login page' : 'Set a password to enable 2FA later',
      },
      {
        label: 'Two-factor',
        value: overview.mfa_enabled ? 'Enabled' : 'Coming soon',
        tone: overview.mfa_enabled ? ('success' as const) : ('info' as const),
        description: overview.mfa_enabled ? "You'll be asked for a code on new devices" : 'TOTP / passkeys are shipping soon',
      },
    ]
  }, [overview])

  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="font-semibold mb-2">Account protection</div>
      {rows.length === 0 ? (
        <div className="text-sm text-white/70">Unable to load security overview.</div>
      ) : (
        <ul>
          {rows.map((r) => (
            <li key={r.label} className="flex items-center justify-between gap-4 py-3 border-t border-white/10 first:border-t-0">
              <div className="min-w-0">
                <div className="text-white/70 text-sm">{r.label}</div>
                <div className="text-white font-medium">{r.value}</div>
                {r.description ? <div className="text-xs text-white/60 mt-0.5">{r.description}</div> : null}
              </div>
              <StatusPill label={pillLabel(r.tone)} tone={normalizeTone(r.tone)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function pillLabel(tone: PillTone | 'action') {
  if (tone === 'success') return 'Protected'
  if (tone === 'warning' || tone === 'action') return 'Action needed'
  if (tone === 'info') return 'Info'
  return 'Info'
}

function normalizeTone(tone: PillTone | 'action'): PillTone {
  return tone === 'action' ? 'warning' : tone
}
